// Minimal Wilson-K + robust RR for per-tray φ

// --- RR diagnostics state (module-local) ---
const __rrDiagState = {
  lastT: new Map(),          // key: `${tray}:${reason}` -> rounded T
  trayCount: new Map(),      // key: tray -> count
  topLastT: new Map(),       // key: tray -> rounded T for last RRKTOP
  topCount: new Map(),       // key: tray -> count
};

export function rachfordRice(z, K, diag) {
  // Safe bisection between 0 and 1
  const f = (V) => {
    let s = 0;
    for (let i = 0; i < z.length; i++) {
      const denom = 1 + V * (K[i] - 1);
      if (Math.abs(denom) < 1e-12) return NaN;
      s += (z[i] * (K[i] - 1)) / denom;
    }
    return s;
  };

  const kStats = () => {
    let kmin = Infinity, kmax = -Infinity;
    for (let i = 0; i < K.length; i++) {
      const k = K[i];
      if (Number.isFinite(k)) {
        if (k < kmin) kmin = k;
        if (k > kmax) kmax = k;
      }
    }
    return { kmin, kmax };
  };

  const emit = (reason, chosenV, extra = {}) => {
    if (!diag || !diag.enable) return;

    const tray = diag.tray ?? -1;
    const T = Number.isFinite(diag.T) ? diag.T : NaN;
    const P = Number.isFinite(diag.P) ? diag.P : NaN;

    // Rate limit: only print when something meaningfully changes.
    // Uses a single global slot to avoid runaway logs during bisection.
    // module-local state to avoid runaway logs during bisection.
    const state = (__rrDiagState._global ||= { lastKey: null });

    const tKey = Number.isFinite(T) ? T.toFixed(2) : String(T);
    const pKey = Number.isFinite(P) ? P.toFixed(0) : String(P);

    // kStats() and f0/f1/f0n/f1n are closed over (computed later in this function)
    const { kmin, kmax } = kStats();
    const kminKey = Number.isFinite(kmin) ? kmin.toExponential(2) : String(kmin);
    const kmaxKey = Number.isFinite(kmax) ? kmax.toExponential(2) : String(kmax);

    const key = `${tray}|${reason}|${tKey}|${pKey}|${kminKey}|${kmaxKey}`;
    if (!diag.force && state.lastKey === key) return;
    state.lastKey = key;

    const _f0 = Number.isFinite(extra.f0) ? extra.f0 : f0;
    const _f1 = Number.isFinite(extra.f1) ? extra.f1 : f1;
    const _f0n = Number.isFinite(extra.f0n) ? extra.f0n : f0n;
    const _f1n = Number.isFinite(extra.f1n) ? extra.f1n : f1n;

    const sumz = Number.isFinite(extra.sumz) ? extra.sumz : zsum;
    const SzK_ = Number.isFinite(extra.SzK) ? extra.SzK : SzK;
    const SzOverK_ = Number.isFinite(extra.SzOverK) ? extra.SzOverK : SzOverK;

    const Vtxt = Number.isFinite(chosenV) ? chosenV.toFixed(3) : String(chosenV);

    console.log(
      `[RRDIAG] tray=${tray} T=${tKey} P=${pKey} ` +
        `f0=${_f0.toExponential(3)} f1=${_f1.toExponential(3)} ` +
        `sumz=${sumz.toExponential(3)} SzK=${SzK_.toExponential(3)} SzOverK=${SzOverK_.toExponential(3)} ` +
        `f0n=${_f0n.toExponential(3)} f1n=${_f1n.toExponential(3)} ` +
        `Kmin=${kminKey} Kmax=${kmaxKey} chosenV=${Vtxt} reason=${reason}`
    );
  };

  // Normalized sign diagnostics (for interpretation only; does NOT change solver behavior)
  const zsum = z.reduce((a, b) => a + b, 0) || 1;
  const zn = z.map((v) => v / zsum);
  const SzK = zn.reduce((s, zi, i) => s + zi * K[i], 0);
  const SzOverK = zn.reduce((s, zi, i) => s + zi / Math.max(1e-30, K[i]), 0);
  const f0n = SzK - 1;
  const f1n = 1 - SzOverK;

  let f0 = f(0);
  let f1 = f(1);
  // Save the endpoint values for diagnostics (we mutate f0/f1 as the bracket tightens).
  const f0_init = f0;
  const f1_init = f1;

  if (!isFinite(f0) || !isFinite(f1)) {
    // fallback: pick by normalized sign tests if possible
    let chosenV = 0.5;
    if (f0n > 0 && f1n > 0) chosenV = 1;
    else if (f0n < 0 && f1n < 0) chosenV = 0;
    emit("NONFINITE_F0F1", chosenV, { f0, f1, f0n, f1n });
    return diag?.returnObject
      ? { V: chosenV, status: "fail", reason: "NONFINITE_F0F1", f0, f1, f0n, f1n }
      : chosenV;
  }

  if (f0 > 0 && f1 > 0) {
    emit("SINGLEPHASE_V", 1, { f0, f1, f0n, f1n });
    return diag?.returnObject
      ? { V: 1, status: "singlePhase", phase: "V", reason: "F0F1_POS", f0, f1, f0n, f1n }
      : 1; // all vapor
  }
  if (f0 < 0 && f1 < 0) {
    emit("SINGLEPHASE_L", 0, { f0, f1, f0n, f1n });
    return diag?.returnObject
      ? { V: 0, status: "singlePhase", phase: "L", reason: "F0F1_NEG", f0, f1, f0n, f1n }
      : 0; // all liquid
  }

  let lo = 0,
    hi = 1;
  for (let it = 0; it < 120; it++) {
    const mid = 0.5 * (lo + hi);
    const fm = f(mid);
    if (!isFinite(fm)) {
      // bisection broke numerically; choose endpoint using current signs if possible
      const chosenV = (f0 > 0) ? 1 : 0;
      emit("NONFINITE_FM", chosenV, { f0, f1, f0n, f1n });
      return diag?.returnObject
        ? { V: chosenV, status: "fail", reason: "NONFINITE_FM", iters: it, f0, f1, f0n, f1n }
        : chosenV;
    }
    if (Math.abs(fm) < 1e-10) {
      // Successful two-phase Rachford–Rice. Emit a single summary line (instead of
      // flooding the console with one line per bisection step).
      emit("TWOPHASE_CONVERGED", mid, { f0: f0_init, f1: f1_init, f0n, f1n });
      return diag?.returnObject
        ? { V: mid, status: "twoPhase", reason: "CONVERGED", iters: it + 1, f0, f1, f0n, f1n }
        : mid;
    }
    if (fm * f0 < 0) {
      hi = mid;
      f1 = fm;
    } else {
      lo = mid;
      f0 = fm;
    }
  }
  const V = 0.5 * (lo + hi);
  // Iteration cap reached: still emit once so you can see that it *did* bracket.
  emit("TWOPHASE_ITER_MAX", V, { f0: f0_init, f1: f1_init, f0n, f1n });
  return diag?.returnObject
    ? { V, status: "twoPhase", reason: "ITER_MAX", iters: 120, f0, f1, f0n, f1n }
    : V;
}


export function phaseCompositions(z, K, V) {
  const eps = 1e-12;
  const x = z.map((zi, i) => {
    const d = 1 + V * (K[i] - 1);
    return Math.abs(d) > eps ? zi / d : 0;
  });
  const y = x.map((xi, i) => K[i] * xi);
  if (!Array.isArray(x)) throw new Error("phaseCompositions(x) got non-array");
  const sx = x.reduce((a, b) => a + b, 0) || 1;
  if (!Array.isArray(y)) throw new Error("phaseCompositions(y) got non-array");
  const sy = y.reduce((a, b) => a + b, 0) || 1;
  return [x.map((v) => v / sx), y.map((v) => v / sy)];
}

export function forceTwoPhaseK(z, K) {
  if (!Array.isArray(z)) throw new Error("forceTwoPhaseK(z) got non-array");
  const sum = z.reduce((a, b) => a + b, 0) || 1;
  const Zi = z.map((zi) => zi / sum);
  let lnKg = 0;
  for (let i = 0; i < K.length; i++)
    lnKg += Zi[i] * Math.log(Math.max(1e-12, Math.min(1e12, K[i])));
  const g = Math.exp(lnKg);
  let Kc = K.map((k) => k / Math.max(1e-12, g));
  if (Kc.every((k) => k >= 1) || Kc.every((k) => k <= 1)) {
    const N = Kc.length,
      mid = (N - 1) / 2,
      beta = 0.12;
    for (let i = 0; i < N; i++) {
      const tilt = Math.exp((beta * (i - mid)) / Math.max(1, mid));
      Kc[i] = Kc[i] * tilt;
    }
    let lnKg2 = 0;
    for (let i = 0; i < N; i++)
      lnKg2 += Zi[i] * Math.log(Math.max(1e-12, Kc[i]));
    const g2 = Math.exp(lnKg2);
    Kc = Kc.map((k) => k / Math.max(1e-12, g2));
  }
  return Kc.map((k) => Math.min(Math.max(k, 1e-3), 1e3));
}